import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Progress as Bar } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Star, Flame, Award, Trophy, HeartHandshake, Loader2, Lock, Shield, Gift, Dumbbell } from 'lucide-react';
import { toast } from 'sonner';
import { progressAPI, leaderboardAPI } from '../services/api';
import { BadgeIcon } from '../components/BadgeIcon';
import { NextGiftCard } from '../components/NextGiftCard';

const BILAN_KEY = 'bfc21_bilan';

export default function Progress() {
  const [data, setData] = useState(null);
  const [indiv, setIndiv] = useState([]);
  const [entraide, setEntraide] = useState([]);
  const [bilan, setBilan] = useState(() => localStorage.getItem(BILAN_KEY) || '');

  useEffect(() => {
    progressAPI.me().then(setData).catch(() => {});
    leaderboardAPI.individual().then(setIndiv).catch(() => {});
    leaderboardAPI.entraide().then(setEntraide).catch(() => {});
  }, []);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-red-600" /></div>;

  const { stats, badges, calendar } = data;
  const saveBilan = () => { localStorage.setItem(BILAN_KEY, bilan); toast.success('Bilan enregistré'); };
  const maxPts = Math.max(20, ...calendar.map((c) => c.record?.points_earned || 0));
  const goalReached = stats.validated_count >= 10;

  return (
    <div className="space-y-6 animate-rise">
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-red-950 dark:text-red-50">📈 Mes progrès</h1>

      {/* Quick links */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { to: '/equipe', label: 'Mon équipe', icon: Shield, testId: 'link-equipe' },
          { to: '/recompenses', label: 'Récompenses', icon: Gift, testId: 'link-recompenses' },
          { to: '/seances', label: 'Séances', icon: Dumbbell, testId: 'link-seances' },
        ].map((l) => (
          <Link key={l.to} to={l.to} data-testid={l.testId} className="p-4 rounded-2xl border border-red-100 dark:border-red-900/50 bg-white dark:bg-red-950/30 flex flex-col items-center gap-2 hover:shadow-md transition-shadow">
            <div className="w-10 h-10 rounded-xl bfc-gradient-warm text-white flex items-center justify-center"><l.icon className="w-5 h-5" /></div>
            <span className="text-xs font-semibold text-foreground text-center">{l.label}</span>
          </Link>
        ))}
      </div>

      {/* Next gift progression (Top 10) */}
      <NextGiftCard />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { icon: Star, v: stats.total_points, l: 'Points', c: 'text-orange-500' },
          { icon: Flame, v: `${stats.best_streak} j`, l: 'Meilleure série', c: 'text-red-500' },
          { icon: Award, v: badges.filter((b) => b.unlocked).length, l: 'Badges', c: 'text-red-600' },
          { icon: Trophy, v: `${stats.validated_count}/21`, l: 'Défis validés', c: 'text-rose-600' },
        ].map((s, i) => (
          <Card key={i} className="p-4 rounded-2xl border-red-100 dark:border-red-900/50">
            <s.icon className={`w-5 h-5 ${s.c}`} />
            <p className="text-xl font-extrabold text-red-950 dark:text-red-50 mt-2">{s.v}</p>
            <p className="text-xs text-muted-foreground">{s.l}</p>
          </Card>
        ))}
      </div>

      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold">Progression globale{goalReached && ' 🟢'}</span>
          <span className={`text-sm font-extrabold ${goalReached ? 'text-green-600' : 'text-red-600'}`}>{stats.progress_percent}%</span>
        </div>
        <Bar value={stats.progress_percent} indicatorClassName={goalReached ? 'bg-green-500' : ''} className="h-3" />
      </Card>

      {/* Points chart */}
      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-red-950 dark:text-red-50">Points par jour</h3>
          {goalReached && (
            <span data-testid="chart-goal-badge" className="text-xs font-bold text-green-600 flex items-center gap-1">🎉 Cap des 10 jours !</span>
          )}
        </div>
        <div className="flex items-end gap-1 h-28">
          {calendar.map((c) => {
            const pts = c.record?.points_earned || 0;
            return (
              <div key={c.day_number} className="flex-1 h-full flex items-end" title={`Jour ${c.day_number}: ${pts} pts`}>
                <div className={`w-full rounded-t transition-colors ${goalReached ? 'bg-green-500/80' : 'bg-red-500/80'}`} style={{ height: `${Math.round((pts / maxPts) * 100)}%`, minHeight: pts ? '6px' : '0' }} />
              </div>
            );
          })}
        </div>
        <div className="flex gap-1 mt-1">
          {calendar.map((c) => (
            <span key={c.day_number} className="flex-1 text-center text-[8px] text-muted-foreground">{c.day_number}</span>
          ))}
        </div>
      </Card>

      {/* Badges grid */}
      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
        <h3 className="font-bold text-red-950 dark:text-red-50 mb-4">Collection de badges</h3>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
          {badges.map((b) => (
            <div key={b.badge_id} className="flex flex-col items-center gap-2 text-center" data-testid={`badge-grid-${b.badge_id}`}>
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${b.unlocked ? 'bfc-gradient-warm text-white' : 'bg-muted text-muted-foreground/40'}`}>
                {b.unlocked ? <BadgeIcon name={b.icon} className="w-8 h-8" /> : <Lock className="w-6 h-6" />}
              </div>
              <span className={`text-[11px] font-medium leading-tight ${b.unlocked ? 'text-foreground' : 'text-muted-foreground'}`}>{b.name}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Leaderboards */}
      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
        <Tabs defaultValue="individual">
          <TabsList className="grid grid-cols-2 w-full rounded-full">
            <TabsTrigger value="individual" data-testid="tab-individual" className="rounded-full"><Trophy className="w-4 h-4 mr-1" />Individuel</TabsTrigger>
            <TabsTrigger value="entraide" data-testid="tab-entraide" className="rounded-full"><HeartHandshake className="w-4 h-4 mr-1" />Entraide</TabsTrigger>
          </TabsList>
          <TabsContent value="individual" className="mt-4 space-y-2">
            {indiv.map((u) => (
              <div key={u.user_id} className={`flex items-center gap-3 p-2.5 rounded-xl ${u.is_me ? 'bg-red-50 dark:bg-red-900/30' : ''}`}>
                <span className="w-6 text-center font-extrabold text-muted-foreground">{u.rank}</span>
                <span className="flex-1 font-semibold text-sm text-foreground">{u.name}{u.is_me && ' (toi)'}</span>
                <span className="text-xs text-muted-foreground">🔥{u.current_streak}</span>
                <span className="text-sm font-extrabold text-orange-500">{u.total_points} pts</span>
              </div>
            ))}
          </TabsContent>
          <TabsContent value="entraide" className="mt-4 space-y-2">
            {entraide.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">Encourage la communauté pour apparaître ici !</p> : entraide.map((u) => (
              <div key={u.user_id} className={`flex items-center gap-3 p-2.5 rounded-xl ${u.is_me ? 'bg-red-50 dark:bg-red-900/30' : ''}`}>
                <span className="w-6 text-center font-extrabold text-muted-foreground">{u.rank}</span>
                <span className="flex-1 font-semibold text-sm text-foreground">{u.name}{u.is_me && ' (toi)'}</span>
                <span className="text-sm font-extrabold text-red-600">{u.encouragements} 💚</span>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </Card>

      {/* Bilan */}
      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
        <h3 className="font-bold text-red-950 dark:text-red-50 mb-1">Mon bilan personnel</h3>
        <p className="text-xs text-muted-foreground mb-3">Ce que j'ai appris, mes plus grandes victoires, les habitudes à garder...</p>
        <Textarea value={bilan} onChange={(e) => setBilan(e.target.value)} data-testid="bilan-textarea" rows={4} className="rounded-xl resize-none" placeholder="Écris ton bilan ici..." />
        <Button onClick={saveBilan} data-testid="save-bilan" className="mt-3 bg-orange-500 hover:bg-orange-600 rounded-full font-semibold">Enregistrer mon bilan</Button>
      </Card>
    </div>
  );
}
